// react
import { useCallback, useEffect, useRef, useState } from 'react';

// insights
import { appInsights, trackEvent } from '../insights';

// props
export interface UseFetchParams {
	isEnabled?: boolean;
	isLazy?: boolean;
	onComplete?: () => void;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onError?: (error: any) => void;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onSuccess?: (data: any) => void;
	options?: object;
	url?: string;
}

export const useFetch = ({
	isEnabled = true,
	isLazy = false,
	onComplete,
	onError,
	onSuccess,
	options = {},
	url,
}: UseFetchParams) => {
	// state
	const [error, setError] = useState<unknown>(null);
	const [isLoading, setIsLoading] = useState(false);

	// refs
	const refOnComplete = useRef(onComplete);
	const refOnError = useRef(onError);
	const refOnSuccess = useRef(onSuccess);
	const refOptions = useRef(options);
	const refUrl = useRef(url);
	// Id of the most recent request for this hook instance. A slower, stale
	// response (fired with an earlier url/params) must never apply its result
	// over a newer one — that is the classic list "hit or miss" race.
	const refLatestOp = useRef<string | null>(null);

	const fetchRequest = useCallback(async (u = refUrl.current, o = refOptions.current) => {
		// set loading
		setIsLoading(true);

		// vars
		const { headers = {}, ...opts } = o;
		const idOperation = crypto.randomUUID();
		refLatestOp.current = idOperation;
		const isStale = () => refLatestOp.current !== idOperation;

		try {
			const req = await fetch(u, {
				...opts,
				headers: {
					...headers,
					'Request-Id': idOperation,
					'X-Operation-Id': idOperation,
				},
			});
			const res = await req.json();

			// A newer request superseded this one — drop the response so it can't
			// overwrite fresher data (or clear loading while a newer fetch runs).
			if (isStale()) {
				return res;
			}

			if (res.error) {
				throw res.error;
			}

			if (refOnSuccess.current) {
				refOnSuccess.current(res);
			}

			// track dependency
			appInsights.trackDependencyData({
				id: idOperation,
				name: u,
				responseCode: res.statusCode.code,
				success: res.ok,
				correlationContext: idOperation,
			});
			trackEvent('DataFetched', { success: true });

			return res;
		} catch (error) {
			if (isStale()) {
				return;
			}
			if (refOnError.current) {
				refOnError.current(error);
			}
			setError(error);
			trackEvent('DataFetchError', { message: error });
		} finally {
			if (!isStale()) {
				if (refOnComplete.current) {
					refOnComplete.current();
				}
				setIsLoading(false);
			}
		}
	}, []);

	const refetch = () => {
		fetchRequest();
	};

	// update url on change
	useEffect(() => {
		refUrl.current = url;
	}, [url]);

	// fetch when isEnabled
	useEffect(() => {
		if (!isLazy && isEnabled) {
			fetchRequest();
		}
	}, [fetchRequest, isEnabled, isLazy]);

	// update options ref when options change
	useEffect(() => {
		refOptions.current = options;
	}, [options]);

	return {
		error,
		fetchRequest,
		isLoading,
		refetch,
	};
};
